import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CalendarDays, CalendarPlus, Inbox, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { APPT_STATUS, APPT_TONE, LEAD_STATUS, apptTypeLabel, fmtDate, fmtDateTime } from "@/lib/format";
import { Btn, Chip, Empty, PageHeader, Pill, Spinner } from "@/components/rp";
import { AppointmentDialog } from "@/components/admin/AppointmentDialog";
import { CalendarioMese } from "@/components/admin/CalendarioMese";

const ORDER = { richiesto: 0, confermato: 1, completato: 2, annullato: 3 };

const ApptRow = ({ a, onStatus, onEdit }) => (
  <div className={`rp-card p-5 flex flex-wrap items-center gap-4 ${a.status === "richiesto" ? "bg-[#E6DDCE]/40 border-[#C8BDAB]" : ""}`} data-testid={`agenda-appointment-${a.id}`}>
    <div className="w-16 text-center shrink-0">
      <p className="text-2xl font-semibold leading-none">{new Date(a.date).getDate()}</p>
      <p className="text-[11px] uppercase tracking-wider text-[#757575] mt-1">{new Date(a.date).toLocaleDateString("it-IT", { month: "short" })}</p>
    </div>
    <div className="flex-1 min-w-[200px]">
      <div className="flex items-center gap-2 flex-wrap"><p className="font-medium">{a.title}</p><Pill tone={APPT_TONE[a.status]}>{APPT_STATUS[a.status]}</Pill></div>
      <p className="text-sm text-[#757575] mt-1">{a.client_name} · {fmtDateTime(a.date)} · {a.duration_min} min · {apptTypeLabel(a.type)}</p>
      <p className="text-sm text-[#757575]">{a.location}{a.notes && ` · ${a.notes}`}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <Btn variant="outline" className="h-9" onClick={() => onEdit(a)} data-testid={`appointment-edit-${a.id}`}>Modifica</Btn>
      {a.status === "richiesto" && <Btn className="h-9" onClick={() => onStatus(a, "confermato")} data-testid={`appointment-confirm-${a.id}`}>Conferma</Btn>}
      {["richiesto", "confermato"].includes(a.status) && <Btn variant="secondary" className="h-9" onClick={() => onStatus(a, "completato")} data-testid={`appointment-complete-${a.id}`}>Completato</Btn>}
      {["richiesto", "confermato"].includes(a.status) && <Btn variant="danger" className="h-9" onClick={() => onStatus(a, "annullato")} data-testid={`appointment-cancel-${a.id}`}>Annulla</Btn>}
    </div>
  </div>
);

const LeadCard = ({ l, onStatus }) => (
  <div className="rp-card p-5 sm:p-6" data-testid={`lead-${l.id}`}>
    <div className="flex flex-wrap justify-between gap-2">
      <div>
        <p className="font-medium text-lg">{l.name}</p>
        <p className="text-sm text-[#757575]">{[l.work_type, l.sqm && `${l.sqm} mq`, l.budget && `Budget ${l.budget}`].filter(Boolean).join(" · ")}</p>
      </div>
      <p className="text-xs text-[#757575]">{fmtDate(l.created_at)}</p>
    </div>
    {l.address && <p className="text-sm mt-3">{l.address}</p>}
    {l.description && <p className="text-sm text-[#757575] mt-2 leading-relaxed">{l.description}</p>}
    <div className="flex flex-wrap gap-2 mt-4">
      {l.phone && <a href={`tel:${l.phone}`}><Btn variant="secondary" className="h-9" data-testid={`lead-call-${l.id}`}><Phone className="h-4 w-4" /> {l.phone}</Btn></a>}
      {l.email && <a href={`mailto:${l.email}`}><Btn variant="secondary" className="h-9" data-testid={`lead-email-${l.id}`}><Mail className="h-4 w-4" /> Email</Btn></a>}
    </div>
    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#EBEBEB]">
      {LEAD_STATUS.map((s) => <Chip key={s.v} active={l.status === s.v} onClick={() => onStatus(l, s.v)} data-testid={`lead-status-${l.id}-${s.v}`}>{s.l}</Chip>)}
    </div>
  </div>
);

export default function AdminAgenda() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = ["leads", "lista"].includes(params.get("tab")) ? params.get("tab") : "cal";
  const [appts, setAppts] = useState(null);
  const [leads, setLeads] = useState(null);
  const [orderAppts, setOrderAppts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [initialDate, setInitialDate] = useState("");

  const load = useCallback(() => {
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data));
    api.get("/admin/calendario/cantieri").then((r) => setOrderAppts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setApptStatus = async (a, status) => {
    try { await api.patch(`/appointments/${a.id}`, { status }); toast.success(`Appuntamento ${APPT_STATUS[status].toLowerCase()}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setLeadStatus = async (l, status) => {
    try { await api.patch(`/admin/leads/${l.id}`, { status }); setLeads((ls) => ls.map((x) => (x.id === l.id ? { ...x, status } : x))); } catch (e) { toast.error(errMsg(e)); }
  };

  const sorted = (appts || []).slice().sort((a, b) => ORDER[a.status] - ORDER[b.status] || new Date(a.date) - new Date(b.date));
  const newLeads = (leads || []).filter((l) => l.status === "nuovo").length;
  const newOn = (d) => { setInitialDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T09:00`); setEdit(null); setOpen(true); };
  const onEvent = (e) => (e.o ? navigate(`/admin/commesse/${e.o.order_id}`) : (setEdit(e.a), setOpen(true)));

  return (
    <div>
      <PageHeader label="Agenda" title={tab === "leads" ? "Richieste preventivo" : "Appuntamenti"}>
        {tab !== "leads" && <Btn onClick={() => { setEdit(null); setInitialDate(""); setOpen(true); }} data-testid="agenda-new-appointment-button"><CalendarPlus className="h-4 w-4" /> Nuovo appuntamento</Btn>}
      </PageHeader>
      <div className="inline-flex p-1 rounded-full bg-[#EBEBEB] mb-6" data-testid="agenda-tabs">
        {[["cal", "Calendario"], ["lista", "Elenco"], ["leads", `Richieste preventivo${newLeads ? ` (${newLeads})` : ""}`]].map(([v, l]) => (
          <button key={v} onClick={() => setParams(v === "cal" ? {} : { tab: v })} data-testid={`agenda-tab-${v}`}
            className={`h-9 px-5 rounded-full text-sm font-medium transition-colors ${tab === v ? "bg-white shadow-sm" : "text-[#757575]"}`}>{l}</button>
        ))}
      </div>
      {tab === "cal" && (!appts ? <Spinner /> : <CalendarioMese appts={appts} orderAppts={orderAppts} onDay={newOn} onEvent={onEvent} />)}
      {tab === "lista" && (
        !appts ? <Spinner /> : !sorted.length ? <Empty icon={CalendarDays} title="Nessun appuntamento" data-testid="agenda-empty" /> : (
          <div className="space-y-3" data-testid="agenda-appointments-list">{sorted.map((a) => <ApptRow key={a.id} a={a} onStatus={setApptStatus} onEdit={(x) => { setEdit(x); setOpen(true); }} />)}</div>
        )
      )}
      {tab === "leads" && (
        !leads ? <Spinner /> : !leads.length ? <Empty icon={Inbox} title="Nessuna richiesta" data-testid="leads-empty" /> : (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="leads-list">{leads.map((l) => <LeadCard key={l.id} l={l} onStatus={setLeadStatus} />)}</div>
        )
      )}
      <AppointmentDialog open={open} onClose={() => setOpen(false)} onSaved={load} appt={edit} initialDate={initialDate} />
    </div>
  );
}
